import type {
  ScenarioBattleDefinition,
  ScenarioDefinition,
  ScenarioScriptedPlan,
  ScenarioTutorialRule,
} from "./types.js";

interface ChapterOptions {
  id: string;
  chapter: number;
  numberLabel: string;
  title: string;
  performanceTitle: string;
  goal: string;
  startLog: string;
  stageId: string;
  seed: number;
  playerNames: readonly [string, string];
  servantIds: readonly [string, string];
  opponentPlans?: Readonly<Partial<Record<number, ScenarioScriptedPlan>>>;
  tutorialRules?: Readonly<Partial<Record<number, ScenarioTutorialRule>>>;
}

function chapter(options: ChapterOptions): ScenarioDefinition {
  const battle: ScenarioBattleDefinition = Object.freeze({
    seed: options.seed,
    stageId: options.stageId,
    playerNames: Object.freeze([...options.playerNames]) as readonly [string, string],
    servantIds: Object.freeze([...options.servantIds]) as readonly [string, string],
    opponentPlans: Object.freeze(options.opponentPlans ?? {}),
    tutorialRules: Object.freeze(options.tutorialRules ?? {}),
  });

  return Object.freeze({
    id: options.id,
    chapter: options.chapter,
    numberLabel: options.numberLabel,
    title: options.title,
    performanceTitle: options.performanceTitle,
    goal: options.goal,
    startLog: options.startLog,
    stageId: options.stageId,
    rules: Object.freeze([]),
    battle,
  });
}

export const CHAPTER_TWO = chapter({
  id: "chapter-02",
  chapter: 2,
  numberLabel: "第二章",
  title: "竜のいる遠足",
  performanceTitle: "夢幻冒険譚《竜の待つ山》",
  goal: "少女の幸福度を13〜15へ着地させる",
  startLog: "少年と少女の夢が重なり、《竜の待つ山》が開いた。",
  stageId: "dragon-adventure",
  seed: 20260722,
  playerNames: ["少女", "少年"],
  servantIds: ["ewer", "ewer"],
});

export const CHAPTER_THREE = chapter({
  id: "chapter-03",
  chapter: 3,
  numberLabel: "第三章",
  title: "黒いひげのサンタ",
  performanceTitle: "反復小景《鈴の鳴らないサンタ》",
  goal: "子供の幸福度を13〜15へ着地させる",
  startLog: "雪の家と同じ失敗が重なり、《鈴の鳴らないサンタ》が始まった。",
  stageId: "fireside",
  seed: 20260723,
  playerNames: ["子供", "サンタ"],
  servantIds: ["projector", "projector"],
});

export const CHAPTER_FOUR = chapter({
  id: "chapter-04",
  chapter: 4,
  numberLabel: "第四章",
  title: "額の外の音",
  performanceTitle: "記憶小景《鳴りやまないアンコール》",
  goal: "演奏者の幸福度を13〜15へ着地させる",
  startLog: "冬の広場と一つの記憶が、《額》の内側へ切り出された。",
  stageId: "winter-plaza",
  seed: 20260724,
  playerNames: ["演奏者", "記憶の少女"],
  servantIds: ["frame", "frame"],
});

export const CHAPTER_FIVE = chapter({
  id: "chapter-05",
  chapter: 5,
  numberLabel: "第五章",
  title: "同じ場所を千回",
  performanceTitle: "追憶小景《積み重なった部屋》",
  goal: "ロボットの幸福度を13〜15へ着地させる",
  startLog: "古い書斎へ複数の時間が重なり、《積み重なった部屋》が開いた。",
  stageId: "rain-study",
  seed: 20260725,
  playerNames: ["ロボット", "持ち主"],
  servantIds: ["pin", "pin"],
});

export const CHAPTER_SIX = chapter({
  id: "chapter-06",
  chapter: 6,
  numberLabel: "第六章",
  title: "七つ目の音",
  performanceTitle: "再演《七つ目の音》",
  goal: "親友の幸福度を13〜15へ着地させる",
  startLog: "六つの記憶と一台のピアノが、最後の《窓》へ重なった。",
  stageId: "layered-stage",
  seed: 20260726,
  playerNames: ["親友", "記憶の主人公"],
  servantIds: ["frame", "frame"],
});

export const STORY_CHAPTERS: readonly ScenarioDefinition[] = Object.freeze([
  CHAPTER_TWO,
  CHAPTER_THREE,
  CHAPTER_FOUR,
  CHAPTER_FIVE,
  CHAPTER_SIX,
]);
